import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import phygitalLogo from "../assets/phygital-logo.svg";
import { LANGUAGES, type LanguageCode } from "../i18n/config";
import { useI18n } from "../i18n/I18nProvider";
import {
  AgentIcon,
  Button,
  ChevronDownIcon,
  CloseIcon,
  DocsIcon,
  FramesIcon,
  Header,
  HomeIcon,
  IntegrationIcon,
  ListIcon,
  Loader,
  MenuIcon,
  ModelsIcon,
  NavItem,
  PlayIcon,
  Search,
  Select,
  Shell,
  Sidebar,
  SidebarSection,
  SkillIcon,
  typographyClass,
} from "../ui-kit";
import { ApiKeyControl } from "./ApiKeyControl";
import { useCatalog } from "./CatalogContext";
import { modelPath, ROUTES } from "./routes";
import { ThemeToggle } from "./ThemeToggle";

/**
 * Разделы навигации. Пути берутся из ROUTES — единственного места, где они объявлены.
 *
 * Первая секция идёт без заголовка: это точки входа в портал, а не категория.
 * Витрина («Обзор») теперь такой же пункт меню, как остальные, — верхних вкладок
 * «Главная/Документация» больше нет, вся навигация живёт в одном сайдбаре.
 */
const ENTRY_ITEMS = [
  { to: ROUTES.home, labelKey: "nav.home", Icon: HomeIcon },
  { to: ROUTES.overview, labelKey: "nav.overview", Icon: DocsIcon },
] as const;

/** Секции ниже каталога моделей. Порядок повторяет макет. */
const NAV_SECTIONS = [
  {
    labelKey: "nav.integrations",
    items: [
      { to: ROUTES.mcp, labelKey: "nav.mcp", Icon: AgentIcon },
      { to: ROUTES.skills, labelKey: "nav.skills", Icon: SkillIcon },
    ],
  },
  { labelKey: "nav.runs", items: [{ to: ROUTES.tasks, labelKey: "nav.tasks", Icon: PlayIcon }] },
  {
    labelKey: "nav.pipelines",
    items: [
      { to: ROUTES.frames, labelKey: "nav.frames", Icon: FramesIcon },
      { to: ROUTES.workspaces, labelKey: "nav.workspaces", Icon: ListIcon },
    ],
  },
  {
    labelKey: "nav.reference",
    items: [{ to: ROUTES.endpoints, labelKey: "nav.endpoints", Icon: IntegrationIcon }],
  },
] as const;

/**
 * Раскрывающийся пункт «Выбрать модель» со списком всех моделей и поиском.
 *
 * `onNavigate` зовётся только там, где мы действительно уходим на страницу:
 * раскрытие списка — это ещё не выбор, и шторку на нём закрывать нельзя.
 */
function ModelPicker({ onNavigate }: { onNavigate: () => void }) {
  const { t, modelDoc } = useI18n();
  const { catalog, loading } = useCatalog();
  const location = useLocation();
  const navigate = useNavigate();

  const onModelRoute = location.pathname.startsWith(ROUTES.catalog);
  const [open, setOpen] = useState(onModelRoute);
  const [query, setQuery] = useState("");

  // при переходе на страницу модели список раскрываем, чтобы было видно, где мы
  useEffect(() => {
    if (onModelRoute) setOpen(true);
  }, [onModelRoute]);

  const items = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const named = (catalog?.items ?? []).map((item) => ({
      ...item,
      name: modelDoc(item.id_global).name ?? item.name,
    }));

    // В витрине встречаются одинаковые названия у разных узлов («Vectorizer», «VEO2»).
    // Чтобы пункты меню не были неразличимы, к таким дописываем slug.
    const counts = new Map<string, number>();
    for (const item of named) counts.set(item.name, (counts.get(item.name) ?? 0) + 1);

    return named
      .map((item) => ({ ...item, ambiguous: (counts.get(item.name) ?? 0) > 1 }))
      .filter((item) =>
        needle === "" ? true : `${item.name} ${item.slug}`.toLowerCase().includes(needle),
      )
      .sort((a, b) => a.name.localeCompare(b.name) || a.slug.localeCompare(b.slug));
  }, [catalog, query, modelDoc]);

  const activeSlug = location.pathname.startsWith(`${ROUTES.catalog}/`)
    ? decodeURIComponent(location.pathname.slice(ROUTES.catalog.length + 1))
    : null;

  return (
    <>
      <NavItem
        active={onModelRoute}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        leading={<ModelsIcon />}
        trailing={
          <ChevronDownIcon
            className="ui-nav-item__chevron"
            style={{ transform: open ? undefined : "rotate(-90deg)" }}
          />
        }
      >
        {t("nav.catalog")}
      </NavItem>

      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-1)", paddingLeft: "var(--spacing-3)" }}>
          <Search
            size="small"
            value={query}
            // в узкой колонке меню длинный плейсхолдер обрезался — здесь короткий
            placeholder={t("catalog.searchShort")}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery("")}
            clearLabel={t("action.clear")}
          />

          {loading && <Loader size="small" label={t("status.loading")} />}

          <NavItem
            active={location.pathname === ROUTES.catalog}
            onClick={() => {
              navigate(ROUTES.catalog);
              onNavigate();
            }}
          >
            {t("nav.allModels")}
          </NavItem>

          {items.map((item) => (
            <NavItem
              key={item.slug}
              active={activeSlug === item.slug}
              onClick={() => {
                navigate(modelPath(item.slug));
                onNavigate();
              }}
            >
              {item.name}
              {item.ambiguous && (
                <span className={typographyClass("caption-11-medium")} style={{ color: "var(--color-text-secondary-v2)" }}>
                  {" "}
                  {item.slug}
                </span>
              )}
            </NavItem>
          ))}

          {!loading && items.length === 0 && (
            <span
              className={typographyClass("caption-11-medium")}
              style={{ color: "var(--color-text-secondary-v2)", padding: "0 var(--nav-item-padding-x)" }}
            >
              {t("catalog.empty")}
            </span>
          )}
        </div>
      )}
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t, language, setLanguage } = useI18n();
  const location = useLocation();

  // Ниже брейкпоинта сайдбар — шторка. Состояние держим здесь: его нужно и
  // кнопке в шапке, и самой шторке, и блокировке прокрутки страницы.
  const [navOpen, setNavOpen] = useState(false);

  const closeNav = useCallback(() => setNavOpen(false), []);

  // Переход по ссылке в меню — это ответ на вопрос «куда идём»: шторку закрываем,
  // иначе выбранная страница осталась бы за ней. Пункты закрывают её и сами:
  // выбор уже активного раздела путь не меняет, и одного этого эффекта мало.
  useEffect(() => setNavOpen(false), [location.pathname]);

  // Шторка существует только ниже брейкпоинта. Если её открыли на узком экране,
  // а потом окно расширили, бургер и подложка уезжают вместе с медиазапросом —
  // а `body[data-nav-open]` остался бы и держал прокрутку страницы.
  useEffect(() => {
    const wide = window.matchMedia("(min-width: 901px)");
    const sync = () => {
      if (wide.matches) setNavOpen(false);
    };
    sync();
    wide.addEventListener("change", sync);
    return () => wide.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    // Шторка перекрывает страницу целиком, поэтому фон под ней не должен ехать.
    document.body.dataset.navOpen = "true";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      delete document.body.dataset.navOpen;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [navOpen]);

  const isActive = (to: string, exact?: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  // Тема и язык: на широком экране — в шапке, на узком места там нет, поэтому
  // они переезжают вниз шторки. Оба контрола управляются контекстом, так что
  // вторая копия в разметке — это тот же переключатель, а не второе состояние.
  const preferences = (
    <>
      <ThemeToggle />
      <Select<LanguageCode>
        size="small"
        value={language}
        options={LANGUAGES.map((item) => ({ label: item.label, value: item.code }))}
        onChange={setLanguage}
      />
    </>
  );

  return (
    <>
      <Header
        leading={
          <Button
            className="ui-header__burger"
            variant="ghost"
            iconOnly
            // iconOnly не рендерит children — глиф идёт слотом startIcon.
            startIcon={navOpen ? <CloseIcon /> : <MenuIcon />}
            aria-label={t(navOpen ? "nav.close" : "nav.open")}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((current) => !current)}
          />
        }
        logo={<img className="ui-header__logo" src={phygitalLogo} alt="Phygital+" />}
        caption={t("app.caption")}
        actions={
          <>
            <ApiKeyControl />
            <span className="ui-header__preferences">{preferences}</span>
          </>
        }
      />
      {/* Сайдбар постоянный, включая витрину: вся навигация портала в одном месте. */}
      <Shell
        sidebar={
          <Sidebar open={navOpen} onClose={closeNav} label={t("nav.menu")}>
            {/* Точки входа в портал — не категория, поэтому без заголовка секции. */}
            <SidebarSection>
              {ENTRY_ITEMS.map((item) => (
                <NavItem
                  key={item.to}
                  as={NavLink}
                  to={item.to}
                  end
                  active={isActive(item.to, true)}
                  leading={<item.Icon />}
                  onClick={closeNav}
                >
                  {t(item.labelKey)}
                </NavItem>
              ))}
            </SidebarSection>

            {/* Каталог моделей — раскрывающийся список, поэтому своя секция. */}
            <SidebarSection label={t("nav.models")}>
              <ModelPicker onNavigate={closeNav} />
            </SidebarSection>

            {NAV_SECTIONS.map((section) => (
              <SidebarSection key={section.labelKey} label={t(section.labelKey)}>
                {section.items.map((item) => (
                  <NavItem
                    key={item.to}
                    as={NavLink}
                    to={item.to}
                    active={isActive(item.to)}
                    leading={<item.Icon />}
                    onClick={closeNav}
                  >
                    {t(item.labelKey)}
                  </NavItem>
                ))}
              </SidebarSection>
            ))}

            <div className="ui-sidebar__preferences">{preferences}</div>
          </Sidebar>
        }
      >
        {children}
      </Shell>
    </>
  );
}
